/**
 * Sentry `beforeSend` — strip bearer credentials out of every URL the SDK is
 * about to transmit.
 *
 * ── WHY THIS IS NEEDED EVEN THOUGH THE PAGE SCRUBS ITS OWN ADDRESS BAR ──────
 * `initSentry` runs in `src/index.js` in production, before React mounts, with
 * no route check — so the PUBLIC estimate page (RD-EST-RESP-001) inherits it.
 * Two credential paths survive a clean address bar:
 *
 *   1. **The fetch breadcrumb.** The page GETs
 *      `…/functions/v1/estimate-response?token=<hmac>` — the token is in the
 *      REQUEST url by server contract, and Sentry's fetch/xhr instrumentation
 *      records `breadcrumb.data.url` verbatim. This is the real leak, and the
 *      one a `history.replaceState` on the page can do nothing about.
 *   2. **A race.** Anything that throws between navigation and the page's own
 *      scrub would carry `event.request.url` with `?token=` still on it.
 *
 * Navigation breadcrumbs (`data.to` / `data.from`) are scrubbed for the same
 * reason: they carry the pre-scrub URL for the life of the session.
 *
 * ── DEFENSIVE BY CONSTRUCTION ───────────────────────────────────────────────
 * A `beforeSend` that throws makes Sentry drop the event, which would silently
 * turn our error reporting off. So the whole body is wrapped and ALWAYS returns
 * the event. `sanitizeUrlForLogging` is itself total (it never throws and fails
 * CLOSED on an unparseable URL), and the scrub mutates in place, so even a
 * mid-way failure ships a partially-scrubbed event rather than nothing.
 *
 * Costs nothing at cold start: no `@sentry/*` import here (the SDK stays in its
 * lazy chunk — see `sentryClient.js`), and `sanitizeUrlForLogging` is already
 * in `main.js` via `errorReporting.js`.
 */

import { sanitizeUrlForLogging } from './sanitizeUrlForLogging';

/**
 * Breadcrumb `data` keys that hold a URL:
 *   • `url`       — fetch / xhr breadcrumbs (the estimate-response GET)
 *   • `to`/`from` — navigation breadcrumbs (history pushes, including the
 *                   `replaceState` the estimate page itself performs)
 */
const BREADCRUMB_URL_KEYS = ['url', 'to', 'from'];

function scrubBreadcrumb(crumb) {
  const data = crumb && typeof crumb === 'object' ? crumb.data : null;
  if (!data || typeof data !== 'object') return;
  BREADCRUMB_URL_KEYS.forEach((key) => {
    if (typeof data[key] === 'string' && data[key]) {
      data[key] = sanitizeUrlForLogging(data[key]);
    }
  });
}

/**
 * @param {object} event - the Sentry event about to be sent.
 * @returns {object} the same event, with credential params redacted.
 */
export function scrubSentryEvent(event) {
  try {
    if (!event || typeof event !== 'object') return event;

    const request = event.request;
    if (request && typeof request === 'object') {
      if (typeof request.url === 'string' && request.url) {
        request.url = sanitizeUrlForLogging(request.url);
      }
      /**
       * `Referer` — the SDK builds `event.request.headers` from
       * `document.referrer` (`@sentry/browser` `getHttpRequestData()`), so a
       * credential that is still sitting in the address bar of the PREVIOUS
       * page rides into the report even when `request.url` is clean. The live
       * case is `/reset-password?token_hash=…`: the page now `replaceState`s the
       * token away on load (KAN-1033), but a referrer captured before that
       * strip, or from another tab, can still carry the full recovery URL.
       *
       * `document.referrer` is always absolute, so the parse branch of
       * `sanitizeUrlForLogging` handles it; the object is checked defensively
       * because this function must stay total.
       */
      const headers = request.headers;
      if (headers && typeof headers === 'object'
        && typeof headers.Referer === 'string' && headers.Referer) {
        headers.Referer = sanitizeUrlForLogging(headers.Referer);
      }

      if (typeof request.query_string === 'string' && request.query_string) {
        // A bare `token=…` is NOT a URL, and `sanitizeUrlForLogging`'s
        // fail-closed branch only cuts at the first `?`/`#` — with neither
        // present it would return the credential VERBATIM. Giving it a
        // throwaway origin runs the real key=value scrubber; the origin is
        // then discarded.
        const scrubbed = sanitizeUrlForLogging(`https://scrubbed.invalid/?${request.query_string}`);
        const q = scrubbed.indexOf('?');
        request.query_string = q === -1 ? '' : scrubbed.slice(q + 1);
      }
    }

    if (Array.isArray(event.breadcrumbs)) {
      event.breadcrumbs.forEach(scrubBreadcrumb);
    }

    return event;
  } catch (_e) {
    // NEVER break error reporting. Returning null here would drop the event
    // entirely, i.e. monitoring goes dark on exactly the errors that confuse
    // this function. The page-level `replaceState` is the primary defense.
    return event;
  }
}

export default scrubSentryEvent;
